import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { usersApi } from '@/features/users/api/usersApi'
import { Avatar, Box, HStack, IconButton } from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiSearch, FiX } from 'react-icons/fi'

const getUserName = (user) => (
  [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username || user.email || String(user.id)
)

const getUserSubtitle = (user) => {
  const email = user.email || ''
  const username = user.username || ''

  if (email && username && email !== username) return `${email} (${username})`
  return email || username
}

export function ActivityLogsFilters({ filters, onFiltersChange, onReset }) {
  const { t } = useTranslation()
  const [searchValue, setSearchValue] = useState(filters.search || '')
  const [selectedActor, setSelectedActor] = useState(null)
  const change = (field, value) => onFiltersChange({ ...filters, [field]: value })
  const submitSearch = () => change('search', searchValue)
  const reset = () => {
    setSearchValue('')
    setSelectedActor(null)
    onReset()
  }

  return (
    <HStack spacing={4} flexWrap="wrap">
      <HStack minW="250px" flex={1}>
        <Input
          placeholder={t('audit.search')}
          value={searchValue}
          onChange={(event) => setSearchValue(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && submitSearch()}
        />
        <IconButton aria-label={t('common.search')} onClick={submitSearch} variant="outline">
          <Box as={FiSearch} />
        </IconButton>
      </HStack>
      <Box minW="160px">
        <Select
          placeholder={t('audit.allActions')}
          value={filters.action || ''}
          onChange={(event) => change('action', event.target.value)}
        >
          <option value="0">{t('audit.create')}</option>
          <option value="1">{t('audit.update')}</option>
          <option value="2">{t('audit.delete')}</option>
          <option value="3">{t('audit.access')}</option>
          <option value="4">{t('audit.syslog')}</option>
        </Select>
      </Box>
      <Box minW="160px">
        <Select
          placeholder={t('audit.allLogTypes')}
          value={filters.log_type || ''}
          onChange={(event) => change('log_type', event.target.value)}
        >
          <option value="system">{t('audit.system')}</option>
          <option value="user">{t('audit.user')}</option>
        </Select>
      </Box>
      <Box minW="220px">
        <AutocompleteSelect
          placeholder={t('audit.allActors')}
          value={selectedActor}
          onChange={(user) => {
            setSelectedActor(user)
            change('actor', user?.id || '')
          }}
          fetchOptions={usersApi.getUsersAutocomplete}
          getOptionLabel={getUserName}
          renderOption={(user, isSelected) => (
            <HStack gap={2} w="full">
              <Avatar.Root size="xs">
                <Avatar.Fallback>
                  {(user.first_name?.[0] || '') + (user.last_name?.[0] || '') || getUserName(user)[0]?.toUpperCase() || '?'}
                </Avatar.Fallback>
              </Avatar.Root>
              <Box minW={0} flex={1}>
                <Box fontWeight={isSelected ? '600' : '400'} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                  {getUserName(user)}
                </Box>
                {getUserSubtitle(user) !== getUserName(user) && (
                  <Box color="#5E6C84" fontSize="12px" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                    {getUserSubtitle(user)}
                  </Box>
                )}
              </Box>
            </HStack>
          )}
          emptyText={t('users.noUsers')}
        />
      </Box>
      <Button variant="ghost" onClick={reset} disabled={!filters.search && !filters.action && !filters.log_type && !filters.actor}>
        <HStack spacing={2}>
          <Box as={FiX} />
          <span>{t('common.reset')}</span>
        </HStack>
      </Button>
    </HStack>
  )
}

export default ActivityLogsFilters
